import { Link, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiClientError } from "@/lib/api-client";
import { useDealDetail } from "./use-deal-detail";
import { DealEditView } from "./deal-edit-view";
import { DealReadOnlyView } from "./deal-readonly-view";

// Phase 7 sub-bucket 5 — deal detail page.
//
// Branches on the unified row state:
//   detail.can_edit === true → <DealEditView>   (inline editor)
//   otherwise                 → <DealReadOnlyView>
//
// 404 / RLS-denied → "Deal not found" card. Same convention as
// mdf-detail-page.tsx:22-54.

export function DealDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const detail = useDealDetail(id || null);

  const notFound =
    detail.error instanceof ApiClientError && detail.error.status === 404;

  return (
    <div>
      <Link
        to="/portal/deals"
        className="mb-6 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to deals
      </Link>

      {detail.isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-12 w-2/3" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : notFound ? (
        <section className="rounded-xl border bg-card p-10 text-center">
          <h2 className="font-display text-base font-semibold tracking-tight">
            Deal not found
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            This deal doesn&rsquo;t exist or isn&rsquo;t attributed to your
            team.
          </p>
          <Button asChild className="mt-4" variant="outline">
            <Link to="/portal/deals">Back to deals</Link>
          </Button>
        </section>
      ) : detail.isError ? (
        <section className="rounded-xl border bg-card p-10 text-center">
          <h2 className="font-display text-base font-semibold tracking-tight">
            Couldn&rsquo;t load deal
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Please refresh the page. If the problem persists, contact support.
          </p>
        </section>
      ) : detail.data ? (
        detail.data.can_edit ? (
          <DealEditView detail={detail.data} />
        ) : (
          <DealReadOnlyView detail={detail.data} />
        )
      ) : null}
    </div>
  );
}
